import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatPopulation } from '@/lib/demographics'
import { cn } from '@/lib/utils'
import { DEFAULT_CHART_COLORS } from './colors'

interface ChartDataItem {
  name: string
  value: number
  percentage: number
  key: string
  [key: string]: string | number
}

interface DemographicChartProps {
  title: string
  data: ChartDataItem[]
  colors?: string[]
  className?: string
}

export function DemographicChart({
  title,
  data,
  colors = DEFAULT_CHART_COLORS,
  className,
}: DemographicChartProps) {
  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
              >
                {data.map((entry, index) => (
                  <Cell
                    key={entry.key}
                    fill={colors[index % colors.length]}
                    className="stroke-background"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload as ChartDataItem
                    return (
                      <div className="bg-popover text-popover-foreground rounded-lg border p-2 shadow-md">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-muted-foreground text-sm">
                          {formatPopulation(item.value)} ({item.percentage}%)
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="mt-2 space-y-1">
          {data.map((item, index) => (
            <div key={item.key} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <span className="text-muted-foreground truncate max-w-[120px]">
                  {item.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium tabular-nums">{item.percentage}%</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
